import { rqlBuilder } from '@extrahorizon/javascript-sdk';
import { useQuery } from '@tanstack/react-query';
import { BloodPressureMeasurement } from 'models/bloodpressure';
import { EmptyParametersError } from 'services/errors/EmptyParametersError';
import { queryKeys, SCHEMA_NAMES, sdk } from 'utils/query';

interface GetBloodPressureMeasurementsVariables {
  userId?: string;
  startDate: number;
  endDate: number;
  symptoms?: string[];
  groupIds?: string[];
  classificationGuidelineId?: string;
}

export const getBloodPressureMeasurements = async (variables: GetBloodPressureMeasurementsVariables) => {
  const { userId, startDate, endDate, symptoms, groupIds, classificationGuidelineId } = variables;

  if (!userId || !groupIds || !classificationGuidelineId || groupIds.length === 0) throw new EmptyParametersError();

  const rql = rqlBuilder()
    .eq('creatorId', userId)
    .eq('status', 'classified')
    .eq('data.classifications.guidelineId', classificationGuidelineId)
    .in('groupIds', groupIds)
    .ge('data.measurementTimestamp', startDate.toString())
    .le('data.measurementTimestamp', endDate.toString())
    .sort('data.measurementTimestamp');

  if (symptoms && symptoms.length) {
    rql.in('data.context.symptoms', symptoms);
  }

  return await sdk.data.documents.findAll<BloodPressureMeasurement>(SCHEMA_NAMES.bloodPressureMeasurements, {
    rql: rql.build(),
  });
};

export const useGetBloodPressureMeasurements = (variables: GetBloodPressureMeasurementsVariables) => {
  const { userId, classificationGuidelineId, startDate, endDate, groupIds, symptoms } = variables;
  return useQuery({
    queryKey: [
      queryKeys.bloodPressureMeasurements,
      userId,
      classificationGuidelineId,
      groupIds,
      startDate,
      endDate,
      symptoms,
    ],
    queryFn: () => getBloodPressureMeasurements(variables),
    enabled: !!userId && !!classificationGuidelineId && !!groupIds?.length,
  });
};
